// packages/api/src/PublicOrganizerService.ts
//
// PUBLIC ORGANIZER PAGE client — the anonymous read behind `/o/<slug>` (ROADMAP
// A2b #54). One query returns the profile, its effective theme, and its upcoming
// and past events, mirroring eventsmagic-api's PublicOrganizerCore.getOrganizerPage.
//
// ⚠ EVERY FIELD HERE IS ALREADY PUBLIC-SHAPED BY THE SERVER. `PublicOrganizerProfile`
// is an allowlist built in the core, not the row — there is no accountId, no status,
// no listing flags to leak. `sections` is the SERVER's decision about what may render,
// not the host's raw enable* switches: a host can ask for a memberships section, and
// until there is a way to take the money the server keeps answering false.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import type { PublicEventTheme } from "./PublicEventService";
import { errorMessages } from "./opResultHelpers";

export interface PublicOrganizerSections {
  memberships?: boolean | null;
  donations?: boolean | null;
  contactUs?: boolean | null;
  news?: boolean | null;
  /** B24.5 — the page offers "email me about new events" (double opt-in). Absent from an older gateway = not offered. */
  followEmail?: boolean | null;
}

export interface PublicOrganizerProfile {
  id?: string | null;
  slug?: string | null;
  name?: string | null;
  summary?: string | null;
  /**
   * A2e #81 — may search engines index this page (the owner's plan includes `search_indexing`, the host listed it, it
   * is published)? Absent or null — an old gateway, or any doubt — means NO: the site sends `noindex, nofollow`.
   */
  searchIndexable?: boolean | null;
  /**
   * A2e #96 — the owner's plan removes our branding (Business): the title and link previews leave out our name. Absent
   * (an older gateway) = false = our name shows — the safe side.
   */
  hidePlatformBranding?: boolean | null;
  /** Rich text (HTML) — SANITIZE before rendering. */
  description?: string | null;
  kind?: string | null;
  logoUploadedFileId?: string | null;
  headerUploadedFileId?: string | null;
  logoUploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  headerUploadedFile?: { id?: string | null; fileUrl?: string | null } | null;
  placeName?: string | null;
  country?: string | null;
  stateProvince?: string | null;
  city?: string | null;
  postalCode?: string | null;
  streetAddress?: string | null;
  /** Always a resolved http(s) URL, or null — safe to use as an href. */
  website?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  socialUrls?: (string | null)[] | null;
  sections?: PublicOrganizerSections | null;
}

export interface PublicOrganizerEvent {
  id?: string | null;
  slug?: string | null;
  title?: string | null;
  subtitle?: string | null;
  /** #62 P6(d2) — the event's Overview: the short line the organizer page shows with it. Absent from an older gateway. */
  summary?: string | null;
  /** First occurrence start — a recurring event's REAL first date, not its stale umbrella column. */
  startsAt?: string | null;
  /** Last occurrence end. */
  endsAt?: string | null;
  /** The next date still to come, or null when they are all behind us. */
  nextOccurrenceAt?: string | null;
  /** How many live occurrences this event has. 0 for a single event. */
  occurrenceCount?: number | null;
  /** single | recurring | agenda. */
  scheduleMode?: string | null;
  isAllDay?: boolean | null;
  timezone?: string | null;
  eventFormat?: string | null;
  /** 'published' | 'completed' | 'canceled' — drives the cancelled marker. */
  eventStatus?: string | null;
  channel?: string | null;
  coverUrl?: string | null;
  coverUploadedFileId?: string | null;
  coverUploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
}

/**
 * ⚠ THE EVENT THEME TYPE, REUSED — not a second shape with the same fields. The
 * organizer page is themed by the same token system and rendered through the same
 * `EventThemeScope`, so a parallel declaration would drift the moment a token is
 * added and leave one of the two surfaces rendering stock colours.
 */
export type PublicOrganizerTheme = PublicEventTheme;

export interface PublicOrganizerPage {
  slug?: string | null;
  profile?: PublicOrganizerProfile | null;
  theme?: PublicOrganizerTheme | null;
  /** The FIRST PAGE of upcoming events, server-rendered. */
  upcomingEvents?: PublicOrganizerEvent[] | null;
  /**
   * ⚠ ALWAYS EMPTY on the page read, by design. Past events are history: most visitors never open
   * the tab, so shipping them to everyone makes every organizer page pay for events nobody looked
   * at. The Past tab fetches its own page through `listEvents` when it is first opened.
   */
  pastEvents?: PublicOrganizerEvent[] | null;
  /** Totals for the WHOLE rail — so a tab can advertise itself and the page knows to offer more. */
  upcomingCount?: number | null;
  pastCount?: number | null;
  /** How many events one page of a rail holds. */
  pageSize?: number | null;
  /** #204 — the causes the page takes gifts for (empty when `profile.sections.donations` is false). Absent from an older gateway. */
  causes?: (PublicCause | null)[] | null;
  /** #204 — gifts here take TEST cards (the account's sales test mode): the page says so. */
  donationsTestMode?: boolean | null;
  /** #281 — the memberships the page sells (empty when `profile.sections.memberships` is false). Absent from an older gateway. */
  membershipTypes?: (PublicMembershipType | null)[] | null;
  /** #281 — memberships here take TEST cards; a test purchase issues a TEST membership (test-mode events only): the page says so. */
  membershipsTestMode?: boolean | null;
}

/** #281 — who one membership covers (mirror of the API's member scope). */
export const MEMBERSHIP_MEMBER_SCOPE = {
  INDIVIDUAL: "individual",
  HOUSEHOLD: "household",
  COMPANY: "company",
} as const;

/**
 * #281 — a membership the organizer page sells: a one-time pass for its duration, at the TYPE's price (the server charges
 * that price whatever the browser sends). ⚠ An allowlist built by the server — only types for sale and sellable now.
 */
export interface PublicMembershipType {
  id: string;
  name?: string | null;
  /** PLAIN text the host wrote (not HTML). */
  description?: string | null;
  /** The host's colour for the type — NOT a theme token (the public site does not paint with it). */
  color?: string | null;
  /** individual | household | company (MEMBERSHIP_MEMBER_SCOPE). */
  memberScope?: string | null;
  /** People it covers; null = no limit. */
  maxMembers?: number | null;
  price?: number | null;
  currencyCode?: string | null;
  /** Valid for this many months from purchase; null = no end date. */
  durationMonths?: number | null;
  /** The buyer pays the fees on top — say so before the card form. */
  feesAdded?: boolean | null;
  /** #204 step 6b — it renews automatically every month | quarter | year (null = bought for its duration). */
  renewsEvery?: string | null;
}

export type OrganizerRail = "upcoming" | "past";

/** #204 — a recent NAMED gift on a cause (the host chose to show donors; anonymous gifts are never listed). */
export interface PublicCauseDonor {
  /** "Ann L." */
  name?: string | null;
  /** Only when the host shows amounts too. */
  amount?: number | null;
  /** The donor's own note, only when they offered it and an event's Goal block shows notes. */
  comment?: string | null;
  receivedAt?: string | null;
}

/** #204 — what a cause's receipt is: none, a plain thank-you receipt, or an official (tax) receipt. */
export const CAUSE_RECEIPT_KIND = {
  NONE: "none",
  PLAIN: "plain",
  OFFICIAL: "official",
} as const;

/**
 * #204 — a cause the organizer page takes gifts for: OPEN now, on a host who can take a card (the server leaves out
 * every other one), with its goal number. ⚠ An allowlist built by the server — no fee mode, no host net, no donor contacts.
 */
export interface PublicCause {
  id: string;
  name?: string | null;
  summary?: string | null;
  /** PLAIN text the host wrote (not HTML). */
  description?: string | null;
  imageUploadedFileId?: string | null;
  imageUploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  currencyCode?: string | null;
  /** Null = no goal shown. */
  goalAmount?: number | null;
  raisedAmount?: number | null;
  donationsCount?: number | null;
  suggestedAmounts?: (number | null)[] | null;
  minAmount?: number | null;
  allowCustomAmount?: boolean | null;
  /**
   * #204 — the fair value of what each gift receives in return (null = nothing): a gift must be more than this, and the
   * cause takes one-time gifts only.
   */
  advantageAmount?: number | null;
  advantageDescription?: string | null;
  /** #204 — an official Canadian receipt: the smallest gift that can still be receipted (the CRA 80% rule); null = no limit. */
  receiptMinAmount?: number | null;
  /** The donor pays the fees on top — say so before the card form. */
  feesAdded?: boolean | null;
  /** none | plain | official (CAUSE_RECEIPT_KIND). */
  receiptKind?: string | null;
  /** A tax receipt here needs the donor's postal address (Canada). */
  receiptNeedsAddress?: boolean | null;
  /**
   * #204 step 4 — its window is open now. The organizer page lists only these; an EVENT's Goal block also shows a closed
   * one (its total stays), and its Donate block offers Give only on these.
   */
  takesGifts?: boolean | null;
  /** Gifts are taken until (ISO, UTC); null = no end. */
  endsAt?: string | null;
  /** The page's IANA zone (the organizer page: the account's default; an event: the event's) — `endsAt` is printed on it. Null = UTC. */
  timezone?: string | null;
  donors?: (PublicCauseDonor | null)[] | null;
  /** #204 — the gift form may offer "show my message with my gift": this cause shows its donors. */
  offersPublicNote?: boolean | null;
}

/**
 * The event fields BOTH reads select.
 *
 * ⚠ ONE LIST, shared by the page read and the rail read. Two copies drift the moment a field is
 * added — and the symptom is a card that renders correctly on first paint and loses a detail the
 * instant "Load more" replaces it, which reads as a rendering bug rather than a query one.
 */
const ORGANIZER_EVENT_FIELDS_BASE = `
  id
  slug
  title
  subtitle
  startsAt
  endsAt
  nextOccurrenceAt
  occurrenceCount
  scheduleMode
  isAllDay
  timezone
  eventFormat
  eventStatus
  channel
  coverUrl
  coverUploadedFileId
  coverUploadedFile {
    id
    fileUrl
    thumbnailUrl
  }
`;


const publicOrganizerPageQuery = (eventFields: string, withSearchIndexable: boolean, withCauses = false, withMemberships = false) => gql`
  query PublicOrganizerPage($params: PublicOrganizerPageInput) {
    publicOrganizerPage(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        slug
        profile {
          id
          slug
          name
          summary
          description
          kind
          logoUploadedFileId
          headerUploadedFileId
          logoUploadedFile {
            id
            fileUrl
            thumbnailUrl
          }
          headerUploadedFile {
            id
            fileUrl
          }
          placeName
          country
          stateProvince
          city
          postalCode
          streetAddress
          website
          phone
          fax
          email
          socialUrls
          ${withSearchIndexable ? 'searchIndexable hidePlatformBranding' : ''}
          sections {
            memberships
            donations
            contactUs
            news
            ${withSearchIndexable ? 'followEmail' : ''}
          }
        }
        theme {
          id
          name
          mode
          tokens
          customCss
        }
        upcomingEvents {
          ${eventFields}
        }
        upcomingCount
        pastCount
        pageSize
        ${withCauses ? PUBLIC_CAUSE_FIELDS : ''}
        ${withMemberships ? PUBLIC_MEMBERSHIP_TYPE_FIELDS : ''}
      }
    }
  }
`;

/**
 * #204 — the causes' fields (the "Support us" section and its donate drawer). ⓘ Declared AFTER the query builder that
 * reads them (it is only CALLED below, once both exist) — `check-event-setup` reads the event fields' block above as
 * holding no `summary`, and a cause has one.
 */
// ⓘ Exported: the event site's Donate / Goal blocks (#204 step 4) select the SAME fields (PublicEventService) — one list,
//   so the event's give drawer never lacks a field the organizer page's has.
export const PUBLIC_CAUSE_FIELD_LIST = `
    id
    name
    summary
    description
    imageUploadedFileId
    imageUploadedFile {
      id
      fileUrl
      thumbnailUrl
    }
    currencyCode
    goalAmount
    raisedAmount
    donationsCount
    suggestedAmounts
    minAmount
    allowCustomAmount
    advantageAmount
    advantageDescription
    receiptMinAmount
    feesAdded
    receiptKind
    receiptNeedsAddress
    takesGifts
    endsAt
    timezone
    donors {
      name
      amount
      comment
      receivedAt
    }
    offersPublicNote
`;

const PUBLIC_CAUSE_FIELDS = `
  causes {
    ${PUBLIC_CAUSE_FIELD_LIST}
  }
  donationsTestMode
`;

/** #281 — the membership types' fields (the "Become a member" section and its buy drawer). Declared after the builder, as above. */
const PUBLIC_MEMBERSHIP_TYPE_FIELDS = `
  membershipTypes {
    id
    name
    description
    color
    memberScope
    maxMembers
    price
    currencyCode
    durationMonths
    feesAdded
    renewsEvery
  }
  membershipsTestMode
`;

const publicOrganizerEventsQuery = (eventFields: string) => gql`
  query PublicOrganizerEvents($params: PublicOrganizerEventsInput) {
    publicOrganizerEvents(params: $params) {
      code
      total
      errors {
        name
        errors
      }
      data {
        ${eventFields}
      }
    }
  }
`;

/**
 * #62 P6(d2) — each event's Overview (`summary`), the line the organizer page shows with it.
 * ⚠⚠ A NEW FIELD ON A WHOLE PAGE'S QUERY IS A DEPLOY-ORDER HAZARD: a gateway that has not been recomposed rejects the
 * WHOLE query, and this one is the entire organizer page (it would 404). So the page asks WITH it and, only when the
 * gateway says it does not know the field, asks again WITHOUT — the page renders, just without the Overview lines.
 */
// ⓘ Exported: the event site's "organizer's events" block (B26) selects the SAME fields (PublicEventService).
export const ORGANIZER_EVENT_FIELDS = `${ORGANIZER_EVENT_FIELDS_BASE}\n  summary`;
// A2e #81's `searchIndexable` rides the same retry: refused, it is absent, and absent means noindex — the safe side.
// …and #96's `hidePlatformBranding`: refused, it is absent, and absent means our name shows — the safe side too.
// #204's causes ride a retry of their own (`refusedUnknownCauses`): refused, the page renders without "Support us" — and
// keeps every other field, rather than falling all the way back to the base query.
// #281's memberships ride one step ahead of them (`refusedUnknownMemberships`): a gateway that knows causes but not
// memberships yet renders "Support us" without "Become a member"; one that knows neither drops both.
const PUBLIC_ORGANIZER_PAGE_QUERY_WITH_MEMBERSHIPS = publicOrganizerPageQuery(ORGANIZER_EVENT_FIELDS, true, true, true);
const PUBLIC_ORGANIZER_PAGE_QUERY_WITH_CAUSES = publicOrganizerPageQuery(ORGANIZER_EVENT_FIELDS, true, true);
const PUBLIC_ORGANIZER_PAGE_QUERY = publicOrganizerPageQuery(ORGANIZER_EVENT_FIELDS, true);
const PUBLIC_ORGANIZER_PAGE_QUERY_BASE = publicOrganizerPageQuery(ORGANIZER_EVENT_FIELDS_BASE, false);
const PUBLIC_ORGANIZER_EVENTS_QUERY = publicOrganizerEventsQuery(ORGANIZER_EVENT_FIELDS);
const PUBLIC_ORGANIZER_EVENTS_QUERY_BASE = publicOrganizerEventsQuery(ORGANIZER_EVENT_FIELDS_BASE);

/**
 * The gateway refused the query because it does not know `summary` / `searchIndexable` / `hidePlatformBranding` /
 * `sections.followEmail` (B24.5) yet (not recomposed) — nothing else. Refused, the page is asked again without them.
 */
export const refusedUnknownSummary = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["']summary["'] on type ["']PublicOrganizerEvent["']|Cannot query field ["'](searchIndexable|hidePlatformBranding)["'] on type ["']PublicOrganizerProfile["']|Cannot query field ["']followEmail["'] on type ["']PublicOrganizerSections["']/.test(
    errorMessages(result).join("\n")
  );

/**
 * #204 — the gateway refused the query because it does not know the page's `causes` / `donationsTestMode` (or a cause
 * field) yet — nothing else. Refused, the page is asked again without them: no "Support us" section, nothing else lost.
 */
export const refusedUnknownCauses = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["'](causes|donationsTestMode)["'] on type ["']PublicOrganizerPage["']|on type ["']PublicCause(Donor)?["']|Unknown type ["']PublicCause/.test(
    errorMessages(result).join("\n")
  );

/**
 * #281 — the gateway refused the query because it does not know the page's `membershipTypes` / `membershipsTestMode` (or
 * a membership type's field) yet — nothing else. Refused, the page is asked again without them: no "Become a member".
 */
export const refusedUnknownMemberships = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["'](membershipTypes|membershipsTestMode)["'] on type ["']PublicOrganizerPage["']|on type ["']PublicMembershipType["']|Unknown type ["']PublicMembershipType/.test(
    errorMessages(result).join("\n")
  );

/** A2e #81 — one page in the sitemap. */
export interface PublicSitemapEntry {
  slug?: string | null;
  updatedAt?: string | null;
}

/** A2e #81 — an organizer's group: its own page (only when indexable) and its indexable events. */
export interface PublicSitemapGroup {
  organizerSlug?: string | null;
  organizerIndexable?: boolean | null;
  organizerUpdatedAt?: string | null;
  events?: PublicSitemapEntry[] | null;
}

/** A2e #81 — every page search engines may index in this space, grouped by organizer (server-decided). */
export interface PublicSitemap {
  organizers?: PublicSitemapGroup[] | null;
  events?: PublicSitemapEntry[] | null;
  generatedAt?: string | null;
  truncated?: boolean | null;
}

const PUBLIC_SITEMAP_QUERY = gql`
  query PublicSitemap {
    publicSitemap {
      code
      errors {
        name
        errors
      }
      data {
        organizers {
          organizerSlug
          organizerIndexable
          organizerUpdatedAt
          events {
            slug
            updatedAt
          }
        }
        events {
          slug
          updatedAt
        }
        generatedAt
        truncated
      }
    }
  }
`;

/** B24 item 5 — one entry of an organizer's calendar feed: an event, or one date of a recurring event. */
export interface PublicOrganizerCalendarEntry {
  /** Stable across reads, so a calendar app updates the entry rather than adding another. */
  uid?: string | null;
  eventId?: string | null;
  eventSlug?: string | null;
  title?: string | null;
  summary?: string | null;
  /** ISO UTC. */
  startsAt?: string | null;
  /** ISO UTC — the start when the event has no end. */
  endsAt?: string | null;
  isAllDay?: boolean | null;
  timezone?: string | null;
  /** Kept in the feed and marked, so a subscriber's copy says it is off. */
  canceled?: boolean | null;
  /** The venue's name + address (only when the event shows its place to everyone), "Online", or null. */
  location?: string | null;
  updatedAt?: string | null;
}

/** B24 item 5 — what an organizer's calendar subscription holds (server-decided: the page's own events and gate). */
export interface PublicOrganizerCalendar {
  slug?: string | null;
  name?: string | null;
  entries?: PublicOrganizerCalendarEntry[] | null;
}

const PUBLIC_ORGANIZER_CALENDAR_QUERY = gql`
  query PublicOrganizerCalendar($params: PublicOrganizerCalendarInput) {
    publicOrganizerCalendar(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        slug
        name
        entries {
          uid
          eventId
          eventSlug
          title
          summary
          startsAt
          endsAt
          isAllDay
          timezone
          canceled
          location
          updatedAt
        }
      }
    }
  }
`;

/** B24.5 — "email me about new events": stores an UNCONFIRMED sign-up; a confirmation link is emailed. */
export interface PublicOrganizerFollowInput {
  slug: string;
  email?: string | null;
  name?: string | null;
  /** Honeypot — leave empty. */
  website?: string | null;
}

/** B24.5 — which page a confirmation or an unsubscribe was about. `slug` is null when the page is gone. */
export interface PublicOrganizerFollowState {
  organizerName?: string | null;
  slug?: string | null;
  email?: string | null;
}

const PUBLIC_ORGANIZER_FOLLOW_MUTATION = gql`
  mutation PublicOrganizerFollow($params: PublicOrganizerFollowInput) {
    publicOrganizerFollow(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        ok
      }
    }
  }
`;

const FOLLOW_STATE_FIELDS = `
      code
      errors {
        name
        errors
      }
      data {
        organizerName
        slug
        email
      }
`;

const PUBLIC_ORGANIZER_FOLLOW_CONFIRM_MUTATION = gql`
  mutation PublicOrganizerFollowConfirm($params: PublicOrganizerFollowTokenInput) {
    publicOrganizerFollowConfirm(params: $params) {
      ${FOLLOW_STATE_FIELDS}
    }
  }
`;

const PUBLIC_ORGANIZER_FOLLOW_UNSUBSCRIBE_MUTATION = gql`
  mutation PublicOrganizerFollowUnsubscribe($params: PublicOrganizerFollowTokenInput) {
    publicOrganizerFollowUnsubscribe(params: $params) {
      ${FOLLOW_STATE_FIELDS}
    }
  }
`;

/**
 * #54b d — a visitor's message to an organizer, from the "Get in touch" card on `/o/<slug>`. Name, email (the reply goes
 * to it) and the message are required; the server decides everything else (the page is live, the form is switched on,
 * the format of each field) and answers each refusal on its field.
 */
export interface PublicOrganizerContactInput {
  slug: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  subject?: string | null;
  message?: string | null;
  /** Honeypot — leave empty; a filled value is silently dropped by the server. */
  website?: string | null;
}

const PUBLIC_ORGANIZER_CONTACT_MUTATION = gql`
  mutation PublicOrganizerContactMessage($params: PublicOrganizerContactInput) {
    publicOrganizerContactMessage(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        ok
      }
    }
  }
`;

export class PublicOrganizerService {
  /**
   * A2e #81 — the sitemap's data (SSR, server only). The server decides everything: listed, live, and on a plan that
   * includes search engine listing. ⚠ A gateway that does not know the query yet answers an error — the caller serves
   * an empty sitemap, never a broken one.
   */
  static async getSitemap(options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(PUBLIC_SITEMAP_QUERY, {}, { fetchPolicy: "network-only", ...options });
  }

  /**
   * Resolve one public organizer page by slug. Always a fresh network read (SSR).
   *
   * ⚠ An unknown or unpublished slug comes back as an EMPTY result with code 0, not
   * an error — the caller renders its own 404.
   */
  static async getOrganizerPage(
    slug: string,
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    const variables = { params: { slug } };
    const withMemberships = await ApiService.executeQuery(PUBLIC_ORGANIZER_PAGE_QUERY_WITH_MEMBERSHIPS, variables, { fetchPolicy: "network-only", ...options });
    const membershipsRefused = refusedUnknownMemberships(withMemberships);
    const causesRefused = refusedUnknownCauses(withMemberships);
    const summaryRefused = refusedUnknownSummary(withMemberships);

    if (!membershipsRefused && !causesRefused && !summaryRefused) {
      return withMemberships;
    }

    // ⓘ A gateway reports every unknown field of one query at once — so a refusal naming ONLY the memberships can still
    //   answer the causes; one naming the causes (or the summary) too goes straight to the narrower reads.
    if (membershipsRefused && !causesRefused && !summaryRefused) {
      const withCauses = await ApiService.executeQuery(PUBLIC_ORGANIZER_PAGE_QUERY_WITH_CAUSES, variables, { fetchPolicy: "network-only", ...options });

      if (!refusedUnknownCauses(withCauses) && !refusedUnknownSummary(withCauses)) {
        return withCauses;
      }
    }

    const result = await ApiService.executeQuery(PUBLIC_ORGANIZER_PAGE_QUERY, variables, { fetchPolicy: "network-only", ...options });

    return refusedUnknownSummary(result)
      ? ApiService.executeQuery(PUBLIC_ORGANIZER_PAGE_QUERY_BASE, variables, { fetchPolicy: "network-only", ...options })
      : result;
  }

  /** B24.5 — sign up for "email me about new events" (BROWSER). The answer is the same whatever the address's state. */
  static async follow(input: PublicOrganizerFollowInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_ORGANIZER_FOLLOW_MUTATION, { params: input }, options);
  }

  /** B24.5 — the confirmation link was used (the button on `/follow`). */
  static async confirmFollow(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_ORGANIZER_FOLLOW_CONFIRM_MUTATION, { params: { token } }, options);
  }

  /** B24.5 — unsubscribe (the button on `/unsubscribe`). */
  static async unsubscribeFollow(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_ORGANIZER_FOLLOW_UNSUBSCRIBE_MUTATION, { params: { token } }, options);
  }

  /**
   * B24 item 5 — an organizer's calendar feed (SSR: events-web writes the .ics from it). An unknown or hidden page is an
   * EMPTY result with code 0; a gateway that does not know the query yet answers an error — the route answers 404.
   */
  static async getCalendar(slug: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(PUBLIC_ORGANIZER_CALENDAR_QUERY, { params: { slug } }, { fetchPolicy: "network-only", ...options });
  }

  /**
   * #54b d — send a message to an organizer (BROWSER, through the same-origin proxy). Anonymous and rate-limited.
   * ⓘ No retry for an older gateway: there is nothing to fall back to, and its refusal reads as "could not be sent".
   */
  static async contactMessage(input: PublicOrganizerContactInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_ORGANIZER_CONTACT_MUTATION, { params: input }, options);
  }

  /**
   * One page of an organizer's upcoming or past events — what a rail's tab asks for when a visitor
   * opens it or presses "Load more". Runs in the BROWSER through the same-origin proxy.
   */
  static async listEvents(
    args: { slug: string; rail: OrganizerRail; offset?: number; limit?: number },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    const variables = {
      params: {
        slug: args.slug,
        rail: args.rail,
        offset: args.offset ?? 0,
        limit: args.limit ?? null,
      },
    };
    const result = await ApiService.executeQuery(PUBLIC_ORGANIZER_EVENTS_QUERY, variables, { fetchPolicy: "network-only", ...options });

    return refusedUnknownSummary(result)
      ? ApiService.executeQuery(PUBLIC_ORGANIZER_EVENTS_QUERY_BASE, variables, { fetchPolicy: "network-only", ...options })
      : result;
  }
}
